import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { getLeaderAdmin, updateLeader, deleteLeader, generateLeaderSlug } from "@/lib/api/leaders.functions";
import { MediaField } from "@/components/admin/MediaPicker";
import { SlugField } from "@/components/admin/SlugField";
import { SortableStringList } from "@/components/admin/SortableStringList";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { portraitFromLeader, type MediaSelection } from "@/lib/media/types";

export const Route = createFileRoute("/admin/leaders/$id")({
  loader: ({ params }) => getLeaderAdmin({ data: { id: params.id } }),
  component: EditLeaderPage,
});

function EditLeaderPage() {
  const leader = Route.useLoaderData();
  const navigate = useNavigate();
  if (!leader) throw notFound();

  const [name, setName] = useState(leader.name);
  const [slug, setSlug] = useState(leader.slug);
  const [navLabel, setNavLabel] = useState(leader.navLabel);
  const [designation, setDesignation] = useState(leader.designation);
  const [qualifications, setQualifications] = useState(leader.qualifications ?? "");
  const [initials, setInitials] = useState(leader.initials ?? "");
  const [status, setStatus] = useState(leader.status);
  const [memberships, setMemberships] = useState<string[]>(
    (leader.memberships as string[])?.length ? (leader.memberships as string[]) : [""],
  );
  const [bio, setBio] = useState<string[]>((leader.bio as string[])?.length ? (leader.bio as string[]) : [""]);
  const [portrait, setPortrait] = useState<MediaSelection>(portraitFromLeader(leader));
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateLeader({
        data: {
          id: leader.id,
          name,
          slug,
          navLabel,
          designation,
          qualifications,
          initials,
          status,
          memberships: memberships.filter(Boolean),
          bio: bio.filter(Boolean),
          portraitMediaId: portrait.mediaId,
          portraitUrl: portrait.url,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm("Delete this leader?")) return;
    await deleteLeader({ data: { id: leader.id } });
    toast.success("Deleted");
    navigate({ to: "/admin/leaders" });
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Edit leader"
        action={
          <Button variant="destructive" onClick={handleDelete}>Delete</Button>
        }
      />
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <SlugField
          title={name}
          slug={slug}
          onSlugChange={setSlug}
          onGenerate={() => generateLeaderSlug({ data: { name, excludeId: leader.id } })}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Nav label</Label>
            <Input value={navLabel} onChange={(e) => setNavLabel(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Initials</Label>
            <Input value={initials} onChange={(e) => setInitials(e.target.value)} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Designation</Label>
          <Input value={designation} onChange={(e) => setDesignation(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Qualifications</Label>
          <Input value={qualifications} onChange={(e) => setQualifications(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MediaField label="Portrait" value={portrait} onChange={setPortrait} />
        <SortableStringList label="Memberships & credentials" items={memberships} onChange={setMemberships} />
        <SortableStringList label="Biography paragraphs" items={bio} onChange={setBio} multiline />
      </div>
      <AdminFormFooter cancelTo="/admin/leaders" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
